import {
  type MouseEventHandler,
  type PropsWithChildren,
  type ReactNode
} from "react";

import { T } from "@repo/transifex";
import { type KioskDisplayProductBasketVariantChoice } from "~/stores/kiosk-session/hooks/use-kiosk-products/types";
import type { CapturedWeight } from "~/stores/kiosk-session/types";
import { formatWeightReading } from "~/utils/weight-units";

import { StampcardRewardBadge } from "../../products/stampcard-reward-badge";
import { ProductImage } from "../../ui/product-image/product-image";
import { QuantityInputControl } from "../../../Inputs/quantity-input-control/quantity-input-control";
import { StampcardCollectBadge } from "../../ui/stampcard-collect-badge/stampcard-collect-badge";

import { AllowanceLineBadge } from "./allowance-line-badge";

type ProductItemLineProps = {
  imageUrl: string | null;
  name: string;
  displayPrice: string | null;
  displayPriceOriginal: string | null;
  depositPrice: string | null;
  quantity: number;
  stamps: number | null;
  isRedeemed: boolean;
  choices: KioskDisplayProductBasketVariantChoice[];
  variantDepositPrices?: Record<string, string>;
  weight: CapturedWeight | null;
  appliedAllowanceType: "unitPrice" | "numberOfProducts" | null;
  incrementDisabled?: boolean;
  onQuantityIncrease: MouseEventHandler<HTMLButtonElement>;
  onQuantityDecrease: MouseEventHandler<HTMLButtonElement>;
};

export const ProductItemLine = ({
  imageUrl,
  name,
  displayPrice,
  displayPriceOriginal,
  depositPrice,
  quantity,
  stamps,
  isRedeemed,
  choices,
  variantDepositPrices = {},
  weight,
  appliedAllowanceType,
  incrementDisabled = false,
  onQuantityIncrease,
  onQuantityDecrease
}: ProductItemLineProps) => {
  // Whatever explains the strikethrough. Null still renders the row, so the
  // original price lines up even for a subsidy that has no badge of its own.
  let discountLabel = null;
  if (appliedAllowanceType) {
    discountLabel = (
      <AllowanceLineBadge
        allowanceType={appliedAllowanceType}
        quantity={quantity}
      />
    );
  } else if (isRedeemed) {
    discountLabel = <ItemReward />;
  }

  return (
    <div className="flex gap-6 text-kiosk-text-primary">
      <ProductImage
        image={imageUrl}
        alt={name}
        className="!h-36 !w-52 self-start"
        height="150"
        width="250"
        quality="50"
      />

      {/* One grid for the whole line: a row sizes to the taller of its label
          and its amount, which is what keeps the two columns in step. Two
          separate stacks cannot, and that is what put a price next to the
          wrong sub-line. */}
      <div className="grid flex-1 grid-cols-[1fr_auto_11rem] items-center gap-x-6 gap-y-2">
        <span className="col-start-1 line-clamp-2 text-ellipsis text-4xl font-semibold">
          {name}
        </span>

        <div className="col-start-2 row-start-1">
          <QuantityInputControl
            quantity={quantity}
            onIncrementClick={(e) => {
              if (isRedeemed) return;

              onQuantityIncrease(e);
            }}
            onDecrementClick={onQuantityDecrease}
            variant="secondary"
            incrementDisabled={incrementDisabled}
          />
        </div>

        <span className="col-start-3 text-right text-4xl font-extrabold">
          {displayPrice}
        </span>

        {depositPrice ? (
          <Row label={<ItemDepositFee />} amount={depositPrice} />
        ) : null}

        {choices.map((choice) => (
          <ItemOption
            key={choice.id}
            choice={choice}
            depositPrice={variantDepositPrices[choice.id]}
          />
        ))}

        {weight ? <Row label={<ItemWeight weight={weight} />} /> : null}

        {discountLabel || displayPriceOriginal ? (
          <Row
            label={discountLabel}
            amount={displayPriceOriginal}
            strikethrough
          />
        ) : null}

        {stamps && !isRedeemed ? (
          <Row label={<ItemStampsEarned count={stamps} />} />
        ) : null}
      </div>
    </div>
  );
};

type RowProps = {
  label?: ReactNode;
  amount?: string | null;
  strikethrough?: boolean;
};

/** Always emits both cells, so a label-only row cannot steal the next amount. */
const Row = ({ label, amount, strikethrough = false }: RowProps) => (
  <>
    <div className="col-start-1">{label}</div>
    <div
      className={`col-start-3 text-right text-4xl text-kiosk-text-secondary ${
        strikethrough ? "line-through" : ""
      }`}
    >
      {amount}
    </div>
  </>
);

const ItemSubLabel = ({ children }: PropsWithChildren) => (
  <p className="text-3xl font-normal italic text-interface-neutral-5">
    {children}
  </p>
);

const ItemOption = ({
  choice: { name, quantity },
  depositPrice
}: {
  choice: KioskDisplayProductBasketVariantChoice;
  depositPrice?: string;
}) => (
  <>
    <Row label={<ItemSubLabel>{`${quantity}x ${name}`}</ItemSubLabel>} />
    {depositPrice ? (
      <Row
        label={
          <div className="pl-8">
            <ItemDepositFee />
          </div>
        }
        amount={depositPrice}
      />
    ) : null}
  </>
);

const ItemWeight = ({ weight }: { weight: CapturedWeight }) => (
  <ItemSubLabel>{formatWeightReading(weight)}</ItemSubLabel>
);

const ItemDepositFee = () => (
  <p className="text-3xl font-normal text-interface-neutral-5">
    <T _str="+ Deposit Fee" />
  </p>
);

const ItemStampsEarned = ({ count }: { count: number }) => (
  <div className="flex items-center gap-2">
    <StampcardCollectBadge count={count} size="sm" />
    <p className="italic">
      <T _str="Earn {count} stamps" count={count} />
    </p>
  </div>
);

const ItemReward = () => (
  <div className="flex items-center gap-2">
    <StampcardRewardBadge size="sm" />
    <p className="italic">
      <T _str="Reward" />
    </p>
  </div>
);
